import { useEffect } from 'react';
import Mousetrap, { ExtendedKeyboardEvent } from 'mousetrap';

type Action = 'keypress' | 'keydown' | 'keyup';

/**
 * Enables listening for key events.
 *
 * When listening for key events while focused on an input field ensure
 * that `allowKeybindings` is set to true.
 *
 * @see https://craig.is/killing/mice#api.bind.text-fields
 */
const useKeybinding = ({
  keys,
  callback,
  action,
  disabled = false,
}: {
  /*
   * The key combo e.g. ['ctrl+s', 'command+s'] or 'ctrl+s'
   * For exact syntax of key combinations check https://craig.is/killing/mice
   */
  keys: string | Array<string>;

  /*
   * The function you want run when keys get triggered
   */
  callback: (event: ExtendedKeyboardEvent, triggeredKey: string) => void;
  /*
   * Specify the type of action it should fire on, if non is provided all will be watched
   */
  action?: Action;
  disabled?: boolean;
}) => {
  useEffect(() => {
    if (disabled) return;

    Mousetrap.bind(keys, callback, action);

    return () => {
      if (disabled) return;
      Mousetrap.unbind(keys);
    };
  }, [action, callback, disabled, keys]);
};

export default useKeybinding;
